import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBusinesses } from '../hooks/useBusinesses';
import { useAuth } from '../contexts/AuthContext';
import { useCity } from '../contexts/CityContext';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

export function BusinessDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedCity } = useCity();
  const { user, isVisitor } = useAuth();
  const { getBusinessById, createReview } = useBusinesses(selectedCity?.id);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewError, setReviewError] = useState(null);
  const [reviewSuccess, setReviewSuccess] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [favoriteError, setFavoriteError] = useState(null);

  // LE-FC-B: reivindicação "Este negócio é meu"
  const [claimState, setClaimState] = useState({ loading: true, isOwner: false, claim: null });
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimForm, setClaimForm] = useState({ contact_whatsapp: '', message: '' });
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [claimSuccess, setClaimSuccess] = useState(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    getBusinessById(id)
      .then(setBusiness)
      .catch(() => setBusiness(null))
      .finally(() => setLoading(false));
  }, [id, getBusinessById]);

  // LE-011: carrega o estado de favorito (se autenticado)
  useEffect(() => {
    if (!id || isVisitor) return;
    let active = true;
    api.get('/favorites/ids')
      .then(({ data }) => { if (active) setFavorited((data.ids || []).includes(Number(id))); })
      .catch(() => {});
    return () => { active = false; };
  }, [id, isVisitor]);

  // LE-FC-B: situação da reivindicação do usuário logado sobre este comércio
  useEffect(() => {
    if (!id || isVisitor) { setClaimState({ loading: false, isOwner: false, claim: null }); return; }
    let active = true;
    api.get(`/businesses/${id}/claims/mine`)
      .then(({ data }) => { if (active) setClaimState({ loading: false, isOwner: !!data.isOwner, claim: data.claim || null }); })
      .catch(() => { if (active) setClaimState({ loading: false, isOwner: false, claim: null }); });
    return () => { active = false; };
  }, [id, isVisitor]);

  useEffect(() => { load(); }, [load]);

  // LE-FC-B: envia o pedido de reivindicação. Pode pedir de novo após uma
  // recusa; enquanto houver pedido PENDENTE o botão é trocado por uma badge.
  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    setClaimLoading(true);
    setClaimError(null);
    setClaimSuccess(null);
    try {
      const { data } = await api.post(`/businesses/${id}/claims`, {
        contact_whatsapp: claimForm.contact_whatsapp.trim() || null,
        message: claimForm.message.trim() || null,
      });
      setClaimState((prev) => ({ ...prev, claim: data }));
      setShowClaimModal(false);
      setClaimSuccess('Pedido enviado! O administrador vai analisar e entrará em contato se necessário.');
    } catch (err) {
      setClaimError(err.response?.data?.error?.message || 'Não foi possível enviar o pedido de reivindicação.');
    } finally {
      setClaimLoading(false);
    }
  };

  const toggleFavorite = async () => {
    if (isVisitor) { navigate('/entrar'); return; }
    setFavoriteLoading(true);
    try {
      setFavoriteError(null);
      if (favorited) {
        await api.delete(`/favorites/${id}`);
        setFavorited(false);
      } else {
        await api.put(`/favorites/${id}`);
        setFavorited(true);
      }
    } catch (err) {
      setFavoriteError('Não foi possível atualizar o favorito.');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleReview = async (e) => {
    e.preventDefault();
    setReviewLoading(true);
    setReviewError(null);
    setReviewSuccess(null);
    try {
      await createReview(id, reviewRating, reviewComment.trim() || undefined);
      setReviewSuccess('Avaliação enviada! Obrigado pela contribuição.');
      setReviewComment('');
      load();
    } catch (err) {
      setReviewError(err.response?.data?.error?.message || 'Não foi possível enviar a avaliação.');
    } finally {
      setReviewLoading(false);
    }
  };

  const categoryLabels = {
    mercado: 'Mercado', farmacia: 'Farmácia', padaria: 'Padaria',
    restaurante: 'Restaurante', servicos: 'Serviços', evento: 'Evento', publico: 'Público'
  };

  const categoryEmojis = {
    mercado: '🛒', farmacia: '💊', padaria: '🥖', restaurante: '🍽️',
    servicos: '🔧', evento: '🎉', publico: '🏛️'
  };

  // LE-021: business_hours é um array com day_of_week numérico (0=Dom … 6=Sáb)
  const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const formatTime = (t) => (t ? String(t).slice(0, 5) : '');

  if (loading) return <Loading message="Carregando comércio..." />;
  if (!business) return (
    <EmptyState
      icon="🏪"
      title="Comércio não encontrado"
      description="O comércio que você procura não está disponível no momento."
      action={<button className="nc-btn-primary" onClick={() => navigate('/mapa')}>Voltar ao mapa</button>}
    />
  );

  return (
    <div className="nc-page">
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: 'var(--nc-surface)', borderBottom: '1px solid var(--nc-border)', padding: '10px 16px',
        display: 'flex', alignItems: 'center', gap: 12
      }}>
        <button onClick={() => navigate(-1)} style={{
          background: 'none', border: 'none', padding: 4, cursor: 'pointer', display: 'flex'
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1f2937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 style={{ fontSize: 16, fontWeight: 600, color: 'var(--nc-text)', margin: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {business.name}
        </h1>
        <button
          onClick={toggleFavorite}
          disabled={favoriteLoading}
          aria-label={favorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
          aria-pressed={favorited}
          title={isVisitor ? 'Entre para favoritar' : favorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
          style={{ background: 'none', border: 'none', padding: 4, cursor: favoriteLoading ? 'wait' : 'pointer' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill={favorited ? '#ef4444' : 'none'} stroke={favorited ? '#ef4444' : '#6b7280'} strokeWidth="1.8">
            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
          </svg>
        </button>
        {favoriteError && (
          <span role="alert" style={{ fontSize: 11, color: 'var(--nc-danger-text)', position: 'absolute', top: 54, right: 16, background: 'var(--nc-surface)', padding: '4px 8px', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }}>
            {favoriteError}
          </span>
        )}
        <button
          aria-label="Compartilhar"
          style={{
            background: 'none', border: 'none', padding: 4, cursor: 'pointer',
            /* Alvo de toque de 44px */
            minWidth: 'var(--nc-touch-min)', minHeight: 'var(--nc-touch-min)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--nc-text-secondary)" strokeWidth="1.8" aria-hidden="true">
            <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
            <path d="M8.59 13.51l6.83 3.98" /><path d="M15.41 6.51l-6.82 3.98" />
          </svg>
        </button>
      </div>

      {/* Cover */}
      <div style={{
        height: 180, background: 'var(--nc-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 48, position: 'relative', overflow: 'hidden'
      }}>
        {business.cover_url ? (
          <img src={business.cover_url} alt={`Capa de ${business.name}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (categoryEmojis[business.category] || '🏪')}
        {business.logo_url && (
          <img
            src={business.logo_url}
            alt={`Logo de ${business.name}`}
            style={{
              position: 'absolute', bottom: 12, left: 16, width: 64, height: 64, borderRadius: 14,
              objectFit: 'cover', border: '3px solid var(--nc-surface)', boxShadow: '0 2px 8px rgba(0,0,0,0.18)'
            }}
          />
        )}
      </div>

      <div className="nc-container" style={{ paddingTop: 16, paddingBottom: 16 }}>
        {/* Info */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{ fontSize: 14, color: 'var(--nc-warning)' }}>
              {'★'.repeat(Math.floor(business.rating || 0))}{'☆'.repeat(5 - Math.floor(business.rating || 0))}
            </span>
            <span className="nc-body-sm">
              {business.rating} ({business.reviewCount ?? business.reviews ?? 0} avaliações)
            </span>
          </div>
          <div className="nc-body-sm">
            {categoryLabels[business.category] || business.category} • {business.address}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {business.isOpen && (
              <span style={{
                fontSize: 11, fontWeight: 600, color: 'var(--nc-on-primary)', background: 'var(--nc-accent)',
                padding: '2px 8px', borderRadius: 10
              }}>Aberto agora</span>
            )}
            {business.is24h && (
              <span style={{
                fontSize: 11, fontWeight: 600, color: 'var(--nc-on-primary)', background: 'var(--nc-primary)',
                padding: '2px 8px', borderRadius: 10
              }}>24h</span>
            )}
            {business.distance != null && (
              <span style={{
                fontSize: 11, color: 'var(--nc-text-secondary)', background: 'var(--nc-surface-alt)',
                padding: '2px 8px', borderRadius: 10
              }}>{typeof business.distance === 'number' ? `${business.distance}m` : business.distance}</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {business.whatsapp && (
            <a
              href={`https://wa.me/${business.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="nc-btn-primary"
              style={{ flex: 1, textAlign: 'center', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </svg>
              WhatsApp
            </a>
          )}
          {business.lat != null && business.lng != null && (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${business.lat},${business.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="nc-btn-outline"
              style={{ flex: 1, textAlign: 'center', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
              </svg>
              Rota
            </a>
          )}
        </div>

        {/* LE-FC-B: reivindicação "Este negócio é meu" */}
        {!isVisitor && !claimState.loading && !claimState.isOwner && (
          <div style={{ marginBottom: 20 }}>
            {claimState.claim?.status === 'PENDING' ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'var(--nc-warning-soft)', border: '1px solid var(--nc-warning-border)', borderRadius: 10,
                padding: '10px 12px', fontSize: 13, color: '#92400e'
              }}>
                <span style={{ fontSize: 18 }}>⏳</span>
                <span>Reivindicação <strong>em análise</strong>. Assim que o administrador decidir você verá aqui.</span>
              </div>
            ) : (
              <>
                {claimSuccess && (
                  <div role="status" style={{
                    background: 'var(--nc-success-soft)', border: '1px solid var(--nc-success-border)', borderRadius: 10,
                    padding: '10px 12px', fontSize: 13, color: 'var(--nc-success-text)', marginBottom: 8
                  }}>{claimSuccess}</div>
                )}
                <button
                  onClick={() => { setClaimError(null); setShowClaimModal(true); }}
                  className="nc-btn-outline"
                  style={{ width: '100%', justifyContent: 'center', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <span style={{ fontSize: 16 }}>🏪</span>
                  Este negócio é meu
                </button>
              </>
            )}
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex', borderBottom: '1px solid var(--nc-border)', marginBottom: 16
        }}>
          {['info', 'produtos', 'avaliacoes'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1, padding: '10px 0', border: 'none', background: 'none',
                fontSize: 13, fontWeight: 500, cursor: 'pointer',
                color: activeTab === tab ? '#0d5c3b' : '#9ca3af',
                borderBottom: activeTab === tab ? '2px solid #0d5c3b' : '2px solid transparent',
                transition: 'all 0.15s ease'
              }}
            >
              {tab === 'info' ? 'Informações' : tab === 'produtos' ? 'Produtos' : 'Avaliações'}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'info' && (
          <div className="nc-fade-in">
            {Array.isArray(business.hours) && business.hours.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', marginBottom: 8 }}>Horário de funcionamento</h3>
                <div style={{ background: 'var(--nc-surface)', borderRadius: 10, border: '1px solid var(--nc-border)', overflow: 'hidden' }}>
                  {business.hours.map(h => {
                    const day = DAY_NAMES[h.day_of_week] || '—';
                    const value = h.is_closed
                      ? 'Fechado'
                      : h.is_24h
                        ? '24 horas'
                        : h.open_time && h.close_time
                          ? `${formatTime(h.open_time)} – ${formatTime(h.close_time)}`
                          : 'Horário não informado';
                    return (
                      <div key={h.id || h.day_of_week} style={{
                        display: 'flex', justifyContent: 'space-between', padding: '10px 14px',
                        borderBottom: '1px solid var(--nc-surface-alt)', fontSize: 13
                      }}>
                        <span style={{ color: 'var(--nc-text-dim)' }}>{day}</span>
                        <span style={{ fontWeight: 500, color: 'var(--nc-text)' }}>{value}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', marginBottom: 8 }}>Contato</h3>
              <div style={{ background: 'var(--nc-surface)', borderRadius: 10, border: '1px solid var(--nc-border)', padding: 12 }}>
                <div style={{ fontSize: 13, color: 'var(--nc-text-dim)' }}>{business.phone || 'Não informado'}</div>
                <div style={{ fontSize: 13, color: 'var(--nc-text-dim)', marginTop: 4 }}>{business.address || 'Endereço não informado'}</div>
              </div>
            </div>

            {business.promotions?.length > 0 && (
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', marginBottom: 8 }}>Promoções ativas</h3>
                {business.promotions.map(promo => (
                  <div key={promo.id} style={{
                    background: 'var(--nc-surface)', borderRadius: 10, border: '1px solid var(--nc-border)',
                    padding: 12, marginBottom: 8
                  }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-danger)' }}>{promo.title}</div>
                    <div style={{ fontSize: 13, color: 'var(--nc-text-dim)', marginTop: 2 }}>{promo.description}</div>
                    {promo.validUntil && (
                      <div className="nc-caption" style={{ marginTop: 4 }}>Válido até {promo.validUntil}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'produtos' && (
          <div className="nc-fade-in">
            {business.products?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {business.products.map(product => (
                  <div key={product.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: 12,
                    background: 'var(--nc-surface)', borderRadius: 10, border: '1px solid var(--nc-border)'
                  }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 8, background: 'var(--nc-surface-alt)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20
                    }}>📦</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--nc-text)' }}>{product.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--nc-text-muted)' }}>{product.available ? 'Disponível' : 'Indisponível'}</div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-primary)' }}>
                      R$ {product.price.toFixed(2).replace('.', ',')}
                      <span className="nc-caption" style={{ fontWeight: 400 }}>/{product.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon="📦" title="Sem produtos cadastrados" description="Este comércio ainda não cadastrou produtos." />
            )}
          </div>
        )}

        {activeTab === 'avaliacoes' && (
          <div className="nc-fade-in">
            {/* Formulário de avaliação */}
            {!isVisitor && (
              <form onSubmit={handleReview} style={{
                background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)',
                padding: 14, marginBottom: 14
              }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', marginBottom: 10 }}>Avalie este comércio</div>
                <div style={{ display: 'flex', gap: 4, marginBottom: 10 }} role="radiogroup" aria-label="Nota">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setReviewRating(n)}
                      aria-label={`${n} estrelas`}
                      aria-pressed={reviewRating === n}
                      style={{
                        background: 'none', border: 'none', fontSize: 26, cursor: 'pointer', padding: 2,
                        opacity: n <= reviewRating ? 1 : 0.25, transition: 'opacity 0.15s ease, transform 0.1s ease'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.15)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <textarea
                  value={reviewComment}
                  onChange={e => setReviewComment(e.target.value)}
                  rows={2}
                  placeholder="Conte como foi sua experiência (opcional)"
                  maxLength={1000}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--nc-border)',
                    fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
                    marginBottom: 8
                  }}
                  aria-label="Comentário da avaliação"
                />
                {reviewError && <div role="alert" style={{ fontSize: 12, color: 'var(--nc-danger-text)', marginBottom: 8 }}>{reviewError}</div>}
                {reviewSuccess && <div role="status" style={{ fontSize: 12, color: 'var(--nc-success-text)', marginBottom: 8 }}>{reviewSuccess}</div>}
                <button type="submit" disabled={reviewLoading} className="nc-btn-primary" style={{ width: '100%' }}>
                  {reviewLoading ? 'Enviando...' : 'Enviar avaliação'}
                </button>
              </form>
            )}
            {isVisitor && (
              <div style={{
                background: 'var(--nc-success-soft)', border: '1px solid var(--nc-success-border)', borderRadius: 10,
                padding: '10px 12px', fontSize: 13, color: 'var(--nc-success-text)', marginBottom: 14
              }}>
                <button onClick={() => navigate('/entrar')} style={{ background: 'none', border: 'none', color: 'var(--nc-primary)', fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                  Entre na sua conta
                </button>{' '}
                para avaliar e favoritar comércios.
              </div>
            )}

            {/* Lista de avaliações */}
            {business.reviewItems?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {business.reviewItems.map(r => (
                  <div key={r.id} style={{
                    background: 'var(--nc-surface)', borderRadius: 10, border: '1px solid var(--nc-border)', padding: 12
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%', background: 'var(--nc-surface-alt)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14
                      }}>{r.user_name?.[0] || '?'}</div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nc-text)', flex: 1 }}>{r.user_name || 'Usuário'}</div>
                      <span style={{ fontSize: 12, color: 'var(--nc-warning)' }}>{'★'.repeat(r.rating)}</span>
                    </div>
                    {r.comment && <div style={{ fontSize: 13, color: 'var(--nc-text-dim)' }}>{r.comment}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon="⭐" title="Ainda sem avaliações" description="Seja o primeiro a avaliar este comércio!" />
            )}
          </div>
        )}
      </div>

      {/* LE-FC-B: modal de reivindicação */}
      {showClaimModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Reivindicar este comércio"
          onClick={() => setShowClaimModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0
          }}
        >
          <form
            onSubmit={handleClaimSubmit}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 480, background: 'var(--nc-surface)',
              borderTopLeftRadius: 16, borderTopRightRadius: 16, padding: '20px 18px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 24 }}>🏪</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--nc-text)' }}>Este negócio é meu</div>
                <div className="nc-hint">{business.name}</div>
              </div>
              <button
                type="button"
                onClick={() => setShowClaimModal(false)}
                aria-label="Fechar"
                style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--nc-text-muted)', cursor: 'pointer' }}
              >✕</button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--nc-text-dim)', margin: '4px 0 14px' }}>
              Você é o responsável por este comércio? Envie o pedido — o administrador vai
              verificar e, se aprovado, você passa a administrá-lo.
            </p>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--nc-text-strong)', marginBottom: 4 }}>
              WhatsApp para contato <span style={{ color: 'var(--nc-text-muted)', fontWeight: 400 }}>(opcional)</span>
            </label>
            <input
              value={claimForm.contact_whatsapp}
              onChange={(e) => setClaimForm((f) => ({ ...f, contact_whatsapp: e.target.value }))}
              placeholder="(83) 99999-0000"
              maxLength={20}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8,
                border: '1px solid var(--nc-border)', fontSize: 13, outline: 'none', marginBottom: 12
              }}
            />

            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--nc-text-strong)', marginBottom: 4 }}>
              Mensagem <span style={{ color: 'var(--nc-text-muted)', fontWeight: 400 }}>(opcional)</span>
            </label>
            <textarea
              value={claimForm.message}
              onChange={(e) => setClaimForm((f) => ({ ...f, message: e.target.value }))}
              rows={3}
              maxLength={2000}
              placeholder="Conte como você se relaciona com o negócio para agilizar a análise"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8,
                border: '1px solid var(--nc-border)', fontSize: 13, outline: 'none',
                resize: 'vertical', fontFamily: 'inherit', marginBottom: 12
              }}
            />

            {claimError && <div role="alert" style={{ fontSize: 12, color: 'var(--nc-danger-text)', marginBottom: 8 }}>{claimError}</div>}

            <button type="submit" disabled={claimLoading} className="nc-btn-primary" style={{ width: '100%' }}>
              {claimLoading ? 'Enviando...' : 'Enviar pedido de reivindicação'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
